import React, { useState } from 'react';

const ToothDiagram = ({ onToothClick, toothNotes = {} }) => {
  const [hoveredTooth, setHoveredTooth] = useState(null);

  // Universal Numbering System (1-32)
  // Upper: 1-16 (right to left)
  // Lower: 17-32 (left to right)
  const upperTeeth = Array.from({ length: 16 }, (_, i) => i + 1);
  const lowerTeeth = Array.from({ length: 16 }, (_, i) => i + 17);

  const getToothColor = (toothNum) => {
    if (toothNotes?.[toothNum]) {
      return '#3b82f6'; // Blue if has notes
    }
    if (hoveredTooth === toothNum) {
      return '#60a5fa'; // Light blue on hover
    }
    return '#ffffff'; // White for realistic tooth color
  };

  const getToothStroke = (toothNum) => {
    if (toothNotes?.[toothNum]) {
      return '#1e40af';
    }
    if (hoveredTooth === toothNum) {
      return '#2563eb';
    }
    return '#9ca3af';
  };

  // Determine tooth type based on position
  const getToothType = (toothNum) => {
    // Convert to position in quadrant (1-8)
    const position = ((toothNum - 1) % 16) % 8 + 1;
    
    if (position === 1 || position === 2) return 'incisor';
    if (position === 3) return 'canine';
    if (position === 4 || position === 5) return 'premolar';
    return 'molar'; // 6, 7, 8
  };

  // Realistic tooth SVG shapes
  const IncisorShape = ({ fill, stroke }) => (
    <g>
      <path
        d="M 0,-12 L 6,-10 L 8,0 L 6,12 L 0,14 L -6,12 L -8,0 L -6,-10 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path
        d="M -4,-8 Q 0,-10 4,-8"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.3"
      />
    </g>
  );

  const CanineShape = ({ fill, stroke }) => (
    <g>
      <path
        d="M 0,-14 L 7,-11 L 9,-2 L 8,8 L 4,14 L 0,15 L -4,14 L -8,8 L -9,-2 L -7,-11 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path
        d="M -3,-10 Q 0,-12 3,-10"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.3"
      />
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="8"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.2"
      />
    </g>
  );

  const PremolarShape = ({ fill, stroke }) => (
    <g>
      <path
        d="M 0,-10 L 8,-8 L 10,-2 L 10,4 L 6,12 L 0,14 L -6,12 L -10,4 L -10,-2 L -8,-8 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path
        d="M -6,-6 L -2,-2 L 2,-2 L 6,-6"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.3"
      />
      <circle
        cx="-3"
        cy="2"
        r="2"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.2"
      />
      <circle
        cx="3"
        cy="2"
        r="2"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.2"
      />
    </g>
  );

  const MolarShape = ({ fill, stroke }) => (
    <g>
      <path
        d="M 0,-12 L 10,-10 L 12,-4 L 12,6 L 8,14 L 0,16 L -8,14 L -12,6 L -12,-4 L -10,-10 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path
        d="M -8,-6 L -4,-2 L 0,0 L 4,-2 L 8,-6"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.3"
      />
      <circle
        cx="-5"
        cy="3"
        r="2.5"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.2"
      />
      <circle
        cx="0"
        cy="5"
        r="2.5"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.2"
      />
      <circle
        cx="5"
        cy="3"
        r="2.5"
        fill="none"
        stroke={stroke}
        strokeWidth="0.5"
        opacity="0.2"
      />
    </g>
  );

  const ToothSVG = ({ toothNum, x, y, isUpper = true }) => {
    const toothType = getToothType(toothNum);
    const fill = getToothColor(toothNum);
    const stroke = getToothStroke(toothNum);
    
    // Render appropriate tooth shape
    const renderToothShape = () => {
      switch (toothType) {
        case 'incisor':
          return <IncisorShape fill={fill} stroke={stroke} />;
        case 'canine':
          return <CanineShape fill={fill} stroke={stroke} />;
        case 'premolar':
          return <PremolarShape fill={fill} stroke={stroke} />;
        case 'molar':
          return <MolarShape fill={fill} stroke={stroke} />;
        default:
          return <IncisorShape fill={fill} stroke={stroke} />;
      }
    };

    return (
      <g
        transform={`translate(${x}, ${y}) ${!isUpper ? 'scale(1, -1)' : ''}`}
        onClick={() => onToothClick(toothNum)}
        onMouseEnter={() => setHoveredTooth(toothNum)}
        onMouseLeave={() => setHoveredTooth(null)}
        style={{ cursor: 'pointer' }}
      >
        {/* Tooth shape */}
        {renderToothShape()}
        
        {/* Tooth number */}
        <text
          x="0"
          y="5"
          textAnchor="middle"
          fontSize="10"
          fontWeight="600"
          fill={toothNotes?.[toothNum] ? '#ffffff' : '#374151'}
          transform={!isUpper ? 'scale(1, -1)' : ''}
        >
          {toothNum}
        </text>
        
        {/* Notes indicator */}
        {toothNotes?.[toothNum] && (
          <circle
            cx="10"
            cy="-10"
            r="4"
            fill="#fbbf24"
            stroke="#ffffff"
            strokeWidth="1"
          />
        )}
      </g>
    );
  };

  const ToothIcon = ({ className }) => (
    <svg 
      className={className} 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M12 2C9 2 7 4 7 7v4c0 2-1 3-2 5s-1 4 0 5c.5.5 1 1 2 1s2-.5 2-2V14c0-1 .5-2 1-2h2c.5 0 1 1 1 2v6c0 1.5 1 2 2 2s1.5-.5 2-1c1-1 1-3 0-5s-2-3-2-5V7c0-3-2-5-5-5z" 
      />
    </svg>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ToothIcon className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Tooth Diagram</h3>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white border-2 border-gray-400 rounded"></div>
            <span className="text-gray-600">No Notes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 border-2 border-blue-700 rounded"></div>
            <span className="text-gray-600">Has Notes</span>
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-b from-blue-50 to-gray-50 rounded-lg p-4">
        <svg
          viewBox="0 0 800 400"
          className="w-full h-auto"
          style={{ maxHeight: '400px' }}
        >
          {/* Upper arch label */}
          <text x="400" y="30" textAnchor="middle" fontSize="14" fontWeight="600" fill="#6b7280">
            Upper Arch
          </text>

          {/* Upper teeth (1-16, right to left) */}
          <g>
            {upperTeeth?.map((toothNum, index) => {
              const x = 50 + index * 46;
              const y = 100;
              return <ToothSVG key={toothNum} toothNum={toothNum} x={x} y={y} isUpper={true} />;
            })}
          </g>

          {/* Arch curve - upper */}
          <path
            d="M 40 100 Q 400 50, 760 100"
            fill="none"
            stroke="#d1d5db"
            strokeWidth="2"
            strokeDasharray="5,5"
          />

          {/* Midline */}
          <line
            x1="400"
            y1="140"
            x2="400"
            y2="260"
            stroke="#d1d5db"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
          <text x="410" y="205" fontSize="12" fill="#9ca3af">Midline</text>

          {/* Arch curve - lower */}
          <path
            d="M 40 300 Q 400 350, 760 300"
            fill="none"
            stroke="#d1d5db"
            strokeWidth="2"
            strokeDasharray="5,5"
          />

          {/* Lower teeth (17-32, left to right) */}
          <g>
            {lowerTeeth?.map((toothNum, index) => {
              const x = 50 + index * 46;
              const y = 300;
              return <ToothSVG key={toothNum} toothNum={toothNum} x={x} y={y} isUpper={false} />;
            })}
          </g>

          {/* Lower arch label */}
          <text x="400" y="380" textAnchor="middle" fontSize="14" fontWeight="600" fill="#6b7280">
            Lower Arch
          </text>

          {/* Quadrant labels */}
          <text x="100" y="200" fontSize="12" fill="#9ca3af">Right</text>
          <text x="700" y="200" fontSize="12" fill="#9ca3af">Left</text>
        </svg>
      </div>
      {hoveredTooth && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Tooth #{hoveredTooth} ({getToothType(hoveredTooth)})</span>
            {toothNotes?.[hoveredTooth] ? (
              <span className="ml-2">- Click to view/edit notes</span>
            ) : (
              <span className="ml-2">- Click to add notes</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default ToothDiagram;